import { useEffect, useState } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Eye, Calculator, Users, FileCheck, MousePointerClick, Euro, Mail } from "lucide-react";
import { api, fmtEur } from "@/lib/api";
import { PageHeader } from "@/components/admin/DashboardLayout";

const STATUS_LABEL = { nuovo: "Nuovi", contattato: "Contattati", preventivo: "Preventivo", chiuso: "Chiusi", perso: "Persi" };

export default function Kpi() {
  const [k, setK] = useState(null);
  useEffect(() => { api.get("/admin/kpi").then((r) => setK(r.data)).catch(() => {}); }, []);
  if (!k) return <p className="text-muted-foreground">Caricamento KPI...</p>;

  const cards = [
    [Eye, "Visite", k.visits], [Calculator, "Calcoli", k.calculations], [Users, "Lead", k.leads], [FileCheck, "Contratti", k.contracts],
    [MousePointerClick, "Click affiliate", k.affiliate_clicks], [Euro, "Revenue", fmtEur(k.revenue)], [Mail, "Iscritti newsletter", k.subscribers],
  ];
  const conv = k.visits ? ((k.leads / k.visits) * 100).toFixed(1) : "0.0";

  return (
    <div data-testid="kpi-page">
      <PageHeader eyebrow="Metriche live" title="Numeri" help="Quante persone visitano il sito, quante usano il calcolatore, quanti lead arrivano e quanto guadagni dai link affiliate. Il grafico mostra gli ultimi 14 giorni." />
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4 mb-8">
        {cards.map(([Icon, l, v]) => (
          <div key={l} className="surface p-5" data-testid={`kpi-card-${l.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2"><Icon size={14} /> {l}</div>
            <p className="font-display text-3xl font-bold">{v}</p>
          </div>
        ))}
        <div className="surface p-5 border-emerald-500/40" data-testid="kpi-card-conversione">
          <p className="text-xs text-muted-foreground mb-2">Conversione visite → lead</p><p className="font-display text-3xl font-bold text-emerald-500">{conv}%</p>
        </div>
      </div>
      <div className="grid gap-5 lg:grid-cols-[1.5fr_1fr]">
        <div className="surface p-5">
          <p className="font-semibold mb-4">Ultimi 14 giorni</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={k.series}>
                <defs><linearGradient id="g1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10B981" stopOpacity={0.4} /><stop offset="100%" stopColor="#10B981" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94A3B8" }} /><YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12, color: "hsl(var(--foreground))" }} />
                <Area type="monotone" dataKey="visite" stroke="#10B981" fill="url(#g1)" strokeWidth={2} />
                <Area type="monotone" dataKey="lead" stroke="#F59E0B" fill="transparent" strokeWidth={2} />
                <Area type="monotone" dataKey="click" stroke="#0EA5E9" fill="transparent" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="space-y-5">
          <div className="surface p-5" data-testid="kpi-funnel">
            <p className="font-semibold mb-4">Pipeline lead</p>
            {Object.entries(k.leads_by_status).map(([s, n]) => (
              <div key={s} className="flex items-center gap-3 mb-2.5 text-sm">
                <span className="w-24 text-muted-foreground">{STATUS_LABEL[s]}</span>
                <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${k.leads ? (n / k.leads) * 100 : 0}%` }} /></div>
                <span className="font-mono text-xs w-6 text-right">{n}</span>
              </div>
            ))}
          </div>
          <div className="surface p-5" data-testid="kpi-channels">
            <p className="font-semibold mb-3">Revenue per canale</p>
            {k.channels.map((c) => <div key={c.name} className="flex justify-between text-sm py-1.5 border-b border-border last:border-0"><span>{c.name}</span><span className="font-mono text-xs text-muted-foreground">{c.clicks} click · {c.conversions} conv · {fmtEur(c.revenue)}</span></div>)}
          </div>
        </div>
      </div>
      <div className="surface p-5 mt-5" data-testid="kpi-top-articles">
        <p className="font-semibold mb-3">Articoli più performanti</p>
        <table className="w-full text-sm"><thead><tr className="text-left text-xs text-muted-foreground"><th className="pb-2">Titolo</th><th className="pb-2 text-right">Visite</th><th className="pb-2 text-right">Click affiliate</th></tr></thead>
          <tbody>{k.top_articles.map((a) => <tr key={a.slug} className="border-t border-border"><td className="py-2.5 pr-4">{a.title}</td><td className="py-2.5 text-right font-mono text-xs">{a.views}</td><td className="py-2.5 text-right font-mono text-xs">{a.clicks}</td></tr>)}</tbody></table>
      </div>
    </div>
  );
}
